/**
 * LlmQuotaBadge — small Topbar indicator showing NOAM quota usage.
 *
 * Visible only when usage ≥ 80% of monthly quota (discreet by design).
 * Click → routes to /app/settings#llm-quota for detailed breakdown.
 *
 * Endpoint: GET /api/workspace/llm-quota
 *   Returns: { plan, limit, used, remaining, percent, year_month, by_endpoint }
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap } from "lucide-react";
import { api } from "@/lib/api";

export default function LlmQuotaBadge() {
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/workspace/llm-quota");
        if (!cancelled) setState(r.data);
      } catch {
        // silent — not critical
      }
    };
    load();
    const t = setInterval(load, 60_000); // refresh every minute
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!state || state.limit === 0) return null;
  const pct = state.percent || 0;
  if (pct < 80) return null;

  const isCritical = pct >= 100;
  const isHigh = pct >= 95;
  const color = isCritical ? "#EF4444" : isHigh ? "#EF9F27" : "#F4D35E";
  const label = isCritical
    ? "Quota NOAM atteint"
    : `Quota NOAM ${pct.toFixed(0)}%`;

  return (
    <Link
      to="/app/settings"
      className="flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] font-medium hover:opacity-90 transition whitespace-nowrap"
      style={{ borderColor: `${color}50`, color, background: `${color}10` }}
      title={`Plan ${state.plan} : ${state.used} / ${state.limit} appels (${state.year_month})`}
      data-testid="llm-quota-badge"
    >
      <Zap className="w-3 h-3" />
      <span>{label}</span>
    </Link>
  );
}
